import React from "react";
import { Form, Spinner } from "react-bootstrap";

const thStyle = {
  background: "#F9FAFB",
  fontWeight: "600",
  fontSize: "13px",
  color: "#374151",
  padding: "12px 16px",
  borderBottom: "1px solid #E5E7EB",
  whiteSpace: "nowrap",
  textAlign: "left",
};

const tdStyle = {
  padding: "12px 16px",
  fontSize: "14px",
  color: "#374151",
  borderBottom: "1px solid #F3F4F6",
  verticalAlign: "middle",
};

const pageBtnStyle = {
  borderRadius: "9999px",
  padding: "6px 16px",
  fontSize: "13px",
  fontWeight: "600",
  border: "1px solid var(--button-color)",
  color: "var(--button-color)",
  background: "#fff",
};

/**
 * Lightweight, generic table for the Inventory module. Deliberately not
 * src/components/Table/Table.jsx - that component is tightly coupled to
 * the appointment/booking domain (imports NewAppointment, fetches doctor
 * availability directly). Mirrors Subscription's inline-styled table look
 * (SubscriptionTable.jsx) instead of the shared .tableHeader/.mobile-card
 * App.css classes, since those classes are also used by
 * Vendor_bookings/ShowAdminView.jsx and must not change.
 *
 * columns: [{ key, label, render?: (row) => node }]
 */
function InventoryTable({
  columns,
  data = [],
  loading = false,
  searchQuery,
  onSearchChange,
  showSearch = true,
  currentPage = 1,
  pageSize = 20,
  totalCount,
  onPageChange,
  onRowClick,
  emptyMessage = "No records found.",
}) {
  const totalPages = totalCount ? Math.max(1, Math.ceil(totalCount / pageSize)) : null;

  const cell = (row, col) => (col.render ? col.render(row) : row[col.key]);

  return (
    <div>
      {showSearch && (
        <div className="mb-3" style={{ maxWidth: 320 }}>
          <Form.Control
            type="text"
            placeholder="Search by name or SKU…"
            value={searchQuery || ""}
            onChange={(e) => onSearchChange?.(e.target.value)}
          />
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" /> Loading…
        </div>
      ) : data.length === 0 ? (
        <div className="text-center text-muted py-5">{emptyMessage}</div>
      ) : (
        <>
          {/* Desktop table */}
          <div
            className="d-none d-md-block"
            style={{
              overflowX: "auto",
              background: "#fff",
              borderRadius: "12px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th key={col.key} style={thStyle}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => (
                  <tr
                    key={row.id ?? idx}
                    onClick={() => onRowClick?.(row)}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FAFB")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    style={{ cursor: onRowClick ? "pointer" : "default", transition: "background 0.15s" }}
                  >
                    {columns.map((col) => (
                      <td key={col.key} style={tdStyle}>{cell(row, col)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="d-md-none d-flex flex-column" style={{ gap: "12px" }}>
            {data.map((row, idx) => (
              <div
                key={row.id ?? idx}
                onClick={() => onRowClick?.(row)}
                style={{
                  cursor: onRowClick ? "pointer" : "default",
                  background: "#fff",
                  borderRadius: "12px",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                  overflow: "hidden",
                }}
              >
                <div style={{ ...thStyle, fontSize: "14px" }}>{cell(row, columns[0])}</div>
                {columns.slice(1).map((col) => (
                  <div
                    key={col.key}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "10px 16px",
                      borderBottom: "1px solid #F3F4F6",
                      fontSize: "13px",
                    }}
                  >
                    <span style={{ color: "#6B7280" }}>{col.label}</span>
                    <span style={{ color: "#374151" }}>{cell(row, col)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}

      {totalPages && totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <button
            style={{ ...pageBtnStyle, opacity: currentPage <= 1 ? 0.5 : 1, cursor: currentPage <= 1 ? "default" : "pointer" }}
            disabled={currentPage <= 1}
            onClick={() => onPageChange(currentPage - 1)}
          >
            Previous
          </button>
          <span style={{ fontSize: 13, color: "#6B7280" }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            style={{ ...pageBtnStyle, opacity: currentPage >= totalPages ? 0.5 : 1, cursor: currentPage >= totalPages ? "default" : "pointer" }}
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(currentPage + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default InventoryTable;
